
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { 
  Background, 
  BasicItem, 
  BastionFacility, 
  Condition, 
  Creature, 
  CreatureAbility, 
  CreatureGift, 
  CreatureGroup, 
  Deity, 
  EldritchInvocation, 
  EncounterComplication, 
  EquipmentPack, 
  Feat, 
  FightingStyle, 
  GameCard, 
  GameClass, 
  GameDeck, 
  LootTable, 
  Gift, 
  MagicItemKeyword, 
  PactBoon, 
  Race, 
  RollableTable, 
  SourceBook, 
  SpecialFeature, 
  Spell, 
  SpellList, 
  SpellSlotType, 
  StandardFeatures, 
  Subclass, 
  Subrace, 
  Table, 
  Environment, 
  Tool, 
  WeaponMasteryProperty 
} from '@dndModels';
import { FloatingFeature, ModelBase, OptionalFeatureBase, ReusableString, Ticket, ErrorLog } from '@models';
import { isDataType, isPlayerObjectType } from '@dndModels/Types';

import CommonUtilities from '@utilities/common_utilities';
import DandDAPI from '@utilities/smart_api';

// import UserEdit from './user/edit';
import SkillEdit from '@dndViews/skill/edit';
import SenseEdit from '@dndViews/sense/edit';
import DeityEdit from '@dndViews/deity/edit';
import EnvironmentEdit from '@dndViews/environment/edit';
import EncounterComplicationEdit from '@dndViews/encounter_complication/edit';
import UserTicketEdit from '@dndViews/user_ticket/edit';
import TicketEdit from '@dndViews/ticket/edit';
import ErrorLogEdit from '@dndViews/error_log/edit';
import SpellEdit from '@dndViews/spell/edit';
import SpellSlotTypeEdit from '@dndViews/spell_slot_type/edit';
import GameCardEdit from '@dndViews/game_card/edit';
import GameDeckEdit from '@dndViews/game_deck/edit';
import TableEdit from '@dndViews/table/edit';
import ArmorTypeEdit from '@dndViews/armor_type/edit';
import LanguageEdit from '@dndViews/language/edit';
import ReusableStringEdit from '@dndViews/reusable_string/edit';
import GiftEdit from '@dndViews/gift/edit';
import BastionFacilityEdit from '@dndViews/bastion_facility/edit';
import BackgroundEdit from '@dndViews/background/edit';
import ItemEdit from '@dndViews/item/edit';
import RollableTableEdit from '@dndViews/rollable_table/edit';
import LootTableEdit from '@dndViews/loot_table/edit';
import CreatureEdit from '@dndViews/creature/edit';
import CreatureGroupEdit from '@dndViews/creature_group/edit';
import CreatureGiftEdit from '@dndViews/creature_gift/edit';
import FeatEdit from '@dndViews/feat/edit';
import EldritchInvocationEdit from '@dndViews/eldritch_invocation/edit';
import FightingStyleEdit from '@dndViews/fighting_style/edit';
import ConditionEdit from '@dndViews/condition/edit';
import PactBoonEdit from '@dndViews/pact_boon/edit';
import GameClassEdit from '@dndViews/game_class/edit';
import SubclassEdit from '@dndViews/subclass/edit';
import EquipmentPackEdit from '@dndViews/equipment_pack/edit';
import MagicItemKeywordEdit from '@dndViews/magic_item_keyword/edit';
import WeaponMasteryPropertyEdit from '@dndViews/weapon_mastery_property/edit';
import RaceEdit from '@dndViews/race/edit';
import SubraceEdit from '@dndViews/subrace/edit';
import SpellListEdit from '@dndViews/spell_list/edit';
import TemplateBaseEdit from '@dndViews/template_base/edit';
import FloatingFeatureEdit from '@dndViews/floating_feature/edit';
import CreatureFeatureEdit from '@dndViews/creature_feature/edit';
import SpecialFeatureEdit from '@dndViews/special_feature/edit';
import ToolEdit from '@dndViews/tool/edit';
import StandardFeaturesEdit from '@dndViews/standard_features/edit';
import LoadingBox from '@components/input/LoadingBox';
import SourceBookEdit from '@dndViews/source_book/edit';

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function StandardEdit() {
  const { data_type, id_or_name, sb_abbrev, name } = useParams();
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [obj, setObj] = useState<ModelBase | undefined>(undefined);
  const [reload_check, setReloadCheck] = useState(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback( async () => {
    if (data_type && isDataType(data_type)) {
      if (id_or_name && id_or_name !== "" && !isPlayerObjectType(data_type)) {
        if (id_or_name === "create") {
          let new_obj = new ModelBase();
          switch (data_type) {
             // Constantized
            case "action_type": break;
            case "armor_type": break;
            case "skill": break;
            case "weapon_keyword": break;
            case "language": break;
            case "sense": break;
             // Not currently in use
            case "org_position": break;
            case "organization": break;
            case "sf_special_attributes": break;
            case "summon": break;
             // No edit
            case "feature_user_rating": break;
            case "model_base": break;
            case "sb_section": break;
            case "template": break;
            case "template_base": break;
            case "feature_source": break;
            case "proficiency": break;
            case "upsertable_base": break;
            case "weapon_maneuver": break;
            // Good
            case "background": new_obj = new Background(); break;
            case "bastion_facility": new_obj = new BastionFacility(); break;
            case "condition": new_obj = new Condition(); break;
            case "creature": new_obj = new Creature(); break;
            case "creature_feature": new_obj = new CreatureAbility(); break;
            case "creature_gift": new_obj = new CreatureGift(); break;
            case "creature_group": new_obj = new CreatureGroup(); break;
            case "deity": new_obj = new Deity(); break;
            case "loot_table": new_obj = new LootTable(); break;
            case "environment": new_obj = new Environment(); break;
            case "encounter_complication": new_obj = new EncounterComplication(); break;
            case "eldritch_invocation": new_obj = new EldritchInvocation(); break;
            case "equipment_pack": new_obj = new EquipmentPack(); break;
            case "feat": new_obj = new Feat(); break;
            case "fighting_style": new_obj = new FightingStyle(); break;
            case "floating_feature": new_obj = new FloatingFeature(); break;
            case "game_card": new_obj = new GameCard(); break;
            case "game_class": new_obj = new GameClass(); break;
            case "game_deck": new_obj = new GameDeck(); break;
            case "gift": new_obj = new Gift(); break;
            case "item": new_obj = new BasicItem(); break;
            case "magic_item_keyword": new_obj = new MagicItemKeyword(); break;
            case "optional_feature_base": new_obj = new OptionalFeatureBase(); break;
            case "pact_boon": new_obj = new PactBoon(); break;
            case "race": new_obj = new Race(); break;
            case "reusable_string": new_obj = new ReusableString(); break;
            case "rollable_table": new_obj = new RollableTable(); break;
            case "source_book": new_obj = new SourceBook(); break;
            case "special_feature": new_obj = new SpecialFeature(); break;
            case "spell": new_obj = new Spell(); break;
            case "spell_list": new_obj = new SpellList(); break;
            case "spell_slot_type": new_obj = new SpellSlotType(); break;
            case "standard_features": new_obj = new StandardFeatures(); break;
            case "subclass": new_obj = new Subclass(); break;
            case "subrace": new_obj = new Subrace(); break;
            case "table": new_obj = new Table(); break;
            case "ticket": new_obj = new Ticket(); break;
            case "error_log": new_obj = new ErrorLog(); break;
            case "tool": new_obj = new Tool(); break;
            case "weapon_mastery_property": new_obj = new WeaponMasteryProperty(); break;
          }
          setObj(api.applyWorkingOn(new_obj));
        } else {
          setLoading(true);
          const onProgress = () => {};
          const res = await api.getObjectsWithCallbacks<ModelBase>({ 
            data_type, 
            onProgress 
          });
          let o = res.find(o => o._id === id_or_name);
          if (!o) {
            let name = id_or_name;
            // Going to need some clean up here to account for spaces, etc.
            const objects_with_name = res.filter(o => common_util.cleanCompare(o.linkifiedName, name) && o.isDominant());
            // Need to check for dominance
            o = objects_with_name[0];
          }
          if (o) {
            await api.loadObject(o);
            setObj(o);
          } 
          setLoading(false);
        }
      } else if (sb_abbrev && name) {
        setLoading(true);
        const onProgress = () => {};
        const res = await api.getObjectsWithCallbacks<ModelBase>({ 
          data_type, 
          onProgress 
        });
        // Going to need some clean up here to account for spaces, etc.
        let clean_name = name;
        let clean_sb_abbrev = sb_abbrev;
        let o = res.find(o => common_util.cleanCompare(o.linkifiedName, clean_name) && o.source_books.some(o2 => common_util.cleanCompare(o2.link_abbrev, clean_sb_abbrev)));
        if (o) {
          await api.loadObject(o);
          setObj(o);
        }
        setLoading(false);
      }
    }
    setLoading(false);
  }, [data_type, id_or_name, sb_abbrev, name]);

  useEffect(() => {
    if (reload_check) {
      setReloadCheck(false);
      if (!obj && data_type && isDataType(data_type) && api.isDataTypeLoaded(data_type)) {
        load();
      }
    }
  }, [reload_check, obj, load]);

  const dataTypeLoaded = () => {
    setReloadCheck(true);
  }

  useEffect(() => {
    console.log("Getting Ready");
    api.subscription_holder.subscribe("Data Type Loaded", mounted, dataTypeLoaded);
  }, []);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);
  
  console.log(obj);
  if (!fetched || loading) {
    return <LoadingBox />;
  } else if (data_type && isDataType(data_type) && obj) {
    // console.log(obj);
    switch (data_type) {
      // case "user": return <UserEdit obj={obj} />;
      case "skill": return <SkillEdit />;
      case "sense": return <SenseEdit />;
      case "armor_type": return <ArmorTypeEdit />;
      case "language": return <LanguageEdit />;
      case "deity": return <DeityEdit obj={obj} />;
      case "environment": return <EnvironmentEdit obj={obj} />;
      case "encounter_complication": return <EncounterComplicationEdit obj={obj} />;
      case "user_ticket": return <UserTicketEdit obj={obj} />;
      case "ticket": return <TicketEdit obj={obj} />;
      case "error_log": return <ErrorLogEdit obj={obj} />;
      case "spell": return <SpellEdit obj={obj} />;
      case "spell_slot_type": return <SpellSlotTypeEdit obj={obj} />;
      case "game_card": return <GameCardEdit obj={obj} />;
      case "game_deck": return <GameDeckEdit obj={obj} />;
      case "table": return <TableEdit obj={obj} />;
      case "reusable_string": return <ReusableStringEdit obj={obj} />;
      case "gift": return <GiftEdit obj={obj} />
      case "bastion_facility": return <BastionFacilityEdit obj={obj} />;
      case "background": return <BackgroundEdit obj={obj} />;
      case "item": return <ItemEdit obj={obj} />;
      case "rollable_table": return <RollableTableEdit obj={obj} />;
      case "loot_table": return <LootTableEdit obj={obj} />;
      case "creature": return <CreatureEdit obj={obj} />;
      case "creature_group": return <CreatureGroupEdit obj={obj} />;
      case "creature_gift": return <CreatureGiftEdit obj={obj} />;
      case "feat": return <FeatEdit obj={obj} />;
      case "eldritch_invocation": return <EldritchInvocationEdit obj={obj} />;
      case "fighting_style": return <FightingStyleEdit obj={obj} />;
      case "condition": return <ConditionEdit obj={obj} />;
      case "pact_boon": return <PactBoonEdit obj={obj} />;
      case "game_class": return <GameClassEdit obj={obj} />;
      case "subclass": return <SubclassEdit obj={obj} />;
      case "equipment_pack": return <EquipmentPackEdit obj={obj} />;
      case "magic_item_keyword": return <MagicItemKeywordEdit obj={obj} />;
      case "weapon_mastery_property": return <WeaponMasteryPropertyEdit obj={obj} />;
      case "race": return <RaceEdit obj={obj} />;
      case "subrace": return <SubraceEdit obj={obj} />;
      case "spell_list": return <SpellListEdit obj={obj} />;
      case "template_base": return <TemplateBaseEdit obj={obj} />;
      case "floating_feature": return <FloatingFeatureEdit obj={obj} />;
      case "creature_feature": return <CreatureFeatureEdit obj={obj} />;
      case "special_feature": return <SpecialFeatureEdit obj={obj} />;
      case "tool": return <ToolEdit obj={obj} />;
      case "standard_features": return <StandardFeaturesEdit obj={obj} />;
      case "source_book": return <SourceBookEdit obj={obj} />;
    }
  }
  return <>Object Not Found</>; // I'll make this go to an actual error page later.
}
